import React, { useEffect } from "react";
import "./KitchenAmenities.css";
import {
  Box,
  Grid,
  Typography,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Divider,
  useMediaQuery,
  useTheme,
} from "@mui/material";

// Define the categories and their respective amenities
const AMENITY_CATEGORIES = {
  Amenities: [
    { name: "parking", label: "Parking Facilities" },
    { name: "dineIn", label: "Dine-In Facilities" },
    { name: "storageSpace", label: "Storage Space" },
    // Add other amenities under this category
  ],
  "Cooking Equipment": [
    { name: "charbroiler", label: "Charbroiler" },
    { name: "convectionOven", label: "Convection Oven" },
    { name: "commercialOven1", label: "Commercial Oven (Single)" },
    { name: "commercialOven2", label: "Commercial Oven (Double)" },
    { name: "deepFryer", label: "Deep Fryer" },
    { name: "griddle", label: "Griddle" },
    { name: "pizzaOven", label: "Pizza Oven" },
    { name: "microwave", label: "Microwave" },
    { name: "steamKettle", label: "Steam Kettle" },
    { name: "rangeStove10Burners", label: "Range/Stove with 10 Burners" },
    { name: "rangeStove6Burners", label: "Range/Stove with 6 Burners" },
    { name: "rangeStove4Burners", label: "Range/Stove with 4 Burners" },
    // Add other cooking equipment
  ],
  "Dishwashing and Cleaning": [
    { name: "cleaningSupplies", label: "Cleaning Supplies" },
    { name: "dishDryingRacks", label: "Dish Drying Racks" },
    { name: "dishwasher", label: "Dishwasher (commercial)" },
    { name: "prepSinks", label: "Prep Sinks" },
    { name: "sink2compartment", label: "Sinks (with 2 compartments)" },
    { name: "sink3compartment", label: "Sinks (with 3 compartments)" },
    // Add other dishwashing and cleaning amenities
  ],
  Facilities: [
    { name: "basicAppliances", label: "Basic Appliances" },
    { name: "fullyEquiped", label: "Fully Equipped" },
    {
      name: "specializedEquipment",
      label: "Specialized Equipment (e.g industrial mixtures)",
    },
    // Add other facilities
  ],
  "Food Preparation": [
    { name: "blender", label: "Blender" },
    { name: "foodProcessor", label: "Food Processor" },
    { name: "mixer", label: "Mixer (Stand or Hand)" },
    { name: "foodScales", label: "Food Scales" },
    { name: "immersionCirculator", label: "Immersion Circulator (sous vide)" },
    { name: "slicer", label: "Slicer (meat and vegetable)" },
    { name: "vaccumSealer", label: "Vacuum Sealer" },
    // Add other food preparation amenities
  ],
  Nearby: [
    { name: "banks", label: "Banks" },
    { name: "eventVenue", label: "Event Venue" },
    { name: "freeway", label: "Freeway" },
    { name: "groceryStore", label: "Grocery Store" },
    { name: "hospitalBuilding", label: "Hospital or Medical Building" },
    { name: "officeBuilding", label: "Office Buildings" },
    { name: "publicParking", label: "Public Parking" },
    { name: "sportsVenue", label: "Sports Venue" },
    // Add other nearby amenities
  ],
  "Refrigeration and Cooling": [
    { name: "commercialRefrigerator", label: "Commercial Refrigerator" },
    {
      name: "displayRefrigerator",
      label: "Display Refrigerator (for showcasing products)",
    },
    { name: "freezer", label: "Freezer" },
    { name: "iceMachine", label: "Ice Machine" },
    { name: "walkInCooler", label: "Walk-in Cooler" },
    // Add other refrigeration and cooling amenities
  ],
  "Safety and Miscellaneous": [
    { name: "fireExtinguisher", label: "Fire Extinguisher" },
    { name: "firstAidKit", label: "First Aid Kit" },
    { name: "greaseTrap", label: "Grease Trap" },
    { name: "hoodSystem", label: "Hood System" },
    {
      name: "safetySignage",
      label: "Safety Signage (e.g., 'Wet floor' signs)",
    },
    { name: "trashCans", label: "Trash Cans and Recycling Bins" },
    { name: "ventilationSystem", label: "Ventilation System" },
    // Add other safety and miscellaneous amenities
  ],
  "Service and Display": [
    {
      name: "buffetLineEquipment",
      label: "Buffet Line Equipment (e.g., chafing dishes)",
    },
    { name: "displayCases", label: "Display Cases" },
    { name: "servingUtensils", label: "Serving Utensils" },
    { name: "plateWarmers", label: "Plate Warmers" },
    { name: "warmingDrawers", label: "Warming Drawers" },
    // Add other service and display amenities
  ],
  "Smallwares and Tools": [
    { name: "bakingSheets", label: "Baking Sheets and Pans" },
    {
      name: "cookingUtensils",
      label: "Cooking Utensils (spatulas, ladles, tongs, etc.)",
    },
    { name: "cuttingBoards", label: "Cutting Boards (various sizes)" },
    { name: "kitchenTimers", label: "Kitchen Timers" },
    { name: "mixingBowls", label: "Mixing Bowls (various sizes)" },
    { name: "foodStorageContainers", label: "Food Storage Containers" },
    { name: "thermometers", label: "Thermometers (food and oven)" },
    { name: "potsAndPans", label: "Pots and Pans (various sizes)" },
    // Add other smallwares and tools
  ],
  "Storage and Organization": [
    { name: "ingredientBins", label: "Ingredient Bins" },
    {
      name: "labelingEquipment",
      label: "Labeling Equipment (e.g., label printer)",
    },
    { name: "prepTables", label: "Prep Tables (stainless steel)" },
    { name: "shelvingUnits", label: "Shelving Units" },
    { name: "storageRacks", label: "Storage Racks" },
    // Add other storage and organization amenities
  ],
  "Usage Type": [
    { name: "catering", label: "Catering" },
    { name: "deliveryOnlyRestaurant", label: "Delivery Only Restaurant" },
    { name: "foodProduction", label: "Food Production" },
    { name: "cookingClasses", label: "Cooking Classes" },
    { name: "cookingWorkshops", label: "Cooking Workshops" },
    { name: "foodPhotoshoots", label: "Food Photoshoots" },
    { name: "popUpRestaurants", label: "Pop-up Restaurants" },
    { name: "foodPreparation", label: "Food Preparation" },
    // Add other usage types
  ],
};

// Create a helper to generate default amenities from AMENITY_CATEGORIES
const getDefaultAmenities = () =>
  Object.values(AMENITY_CATEGORIES)
    .flat()
    .reduce((acc, amenity) => {
      acc[amenity.name] = false;
      return acc;
    }, {});

const KitchenAmenities = ({ editMode, kitchenData, updateKitchenData }) => {
  // Use parent's amenities state if available; otherwise, use default values.
  const amenities = kitchenData.amenities || getDefaultAmenities();

  useEffect(() => {
    const fetched = kitchenData.amenities;
    if (fetched && Object.keys(fetched).length > 0) {
      // Check if fetched data is grouped (has array values)
      const isGrouped = Object.values(fetched).some((val) =>
        Array.isArray(val)
      );
      if (isGrouped) {
        const defaultMapping = getDefaultAmenities();
        Object.entries(AMENITY_CATEGORIES).forEach(([category, list]) => {
          if (fetched[category] && Array.isArray(fetched[category])) {
            list.forEach((amenity) => {
              if (fetched[category].includes(amenity.label)) {
                defaultMapping[amenity.name] = true;
              }
            });
          }
        });
        updateKitchenData({ amenities: defaultMapping });
      }
    }
  }, [editMode, kitchenData.amenities, updateKitchenData]);

  const handleAmenityChange = (e) => {
    const { name, checked } = e.target;
    const updatedAmenities = { ...amenities, [name]: checked };
    updateKitchenData({ amenities: updatedAmenities });
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box
      sx={{
        p: isMobile ? 2 : 4,
        background: "#fff",
        overflowY: isMobile ? "auto" : "visible",
      }}
    >
      <Typography variant="h6" gutterBottom>
        Kitchen Amenities
      </Typography>

      {Object.entries(AMENITY_CATEGORIES).map(([category, list]) => (
        <Box key={category} sx={{ mt: 4 }}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 2 }}>
            {category}
          </Typography>

          <Grid container spacing={2}>
            {list.map((amenity) => (
              <Grid item xs={12} sm={6} md={4} key={amenity.name}>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={amenities[amenity.name] || false}
                        onChange={handleAmenityChange}
                        name={amenity.name}
                      />
                    }
                    label={amenity.label}
                  />
                </FormGroup>
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}
    </Box>
  );
};

export default KitchenAmenities;
export { AMENITY_CATEGORIES };
